import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import { SET_ERROR, SET_SUCCESS } from "../../store/Types/AuthTypes";
import { usePermissions } from "../../permissions/PermissionContext";

export default function RolesMain() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await backendVerifiedUser.get("/roles");
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      dispatch({ type: SET_ERROR, payLoad: err.response?.data?.error?.msg || "Could not load roles" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const confirmDelete = async () => {
    try {
      const res = await backendVerifiedUser.post("/roles/delete", { ids: [toDelete.id] });
      dispatch({ type: SET_SUCCESS, payLoad: res.data?.success?.msg });
      setToDelete(null);
      load();
    } catch (err) {
      dispatch({ type: SET_ERROR, payLoad: err.response?.data?.error?.msg || "Could not delete role" });
      setToDelete(null);
    }
  };

  const nameTemplate = (r) => (
    <div className="flex flex-col">
      <span className="font-semibold">{r.name}</span>
      {r.description && <span className="text-xs text-gray-500">{r.description}</span>}
    </div>
  );

  const typeTemplate = (r) =>
    r.is_super ? (
      <Tag value="Full access" severity="success" />
    ) : r.is_system ? (
      <Tag value="System" severity="info" />
    ) : (
      <Tag value="Custom" />
    );

  const accessTemplate = (r) => (r.is_super ? "All modules" : `${r.module_count} module${r.module_count === 1 ? "" : "s"}`);

  const actionsTemplate = (r) => (
    <div className="flex gap-1">
      {can("roles", "edit") && (
        <Button
          icon={r.is_super ? "pi pi-eye" : "pi pi-pencil"}
          rounded
          text
          tooltip={r.is_super ? "View" : "Edit permissions"}
          onClick={() => navigate(`/edit-role/${r.id}`)}
        />
      )}
      {can("roles", "delete") && !r.is_system && (
        <Button icon="pi pi-trash" rounded text severity="danger" tooltip="Delete" onClick={() => setToDelete(r)} />
      )}
    </div>
  );

  return (
    <div className="mx-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-sm text-gray-600 max-w-2xl">
          Create roles with View / Create / Edit / Delete access per module, then assign a role to each admin user
          from <strong>Manage Users</strong>.
        </p>
        {can("roles", "create") && (
          <Button label="Add Role" icon="pi pi-plus" className="p-primary-btn" onClick={() => navigate("/add-role")} />
        )}
      </div>

      <div className="my-4 card shadow-sm rounded-lg overflow-hidden">
        <DataTable value={roles} loading={loading} dataKey="id" emptyMessage="No roles found." stripedRows>
          <Column header="Role" body={nameTemplate} />
          <Column header="Type" body={typeTemplate} style={{ width: 140 }} />
          <Column header="Access" body={accessTemplate} style={{ width: 140 }} />
          <Column header="Users" field="user_count" style={{ width: 100 }} />
          <Column header="" body={actionsTemplate} style={{ width: 120 }} />
        </DataTable>
      </div>

      <Dialog
        header="Delete role?"
        visible={!!toDelete}
        style={{ width: "min(400px, 95vw)" }}
        onHide={() => setToDelete(null)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setToDelete(null)} />
            <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={confirmDelete} />
          </div>
        }
      >
        <p className="m-0 text-sm">
          <strong>{toDelete?.name}</strong> will be removed. Roles that are still assigned to users can't be deleted.
        </p>
      </Dialog>
    </div>
  );
}
